// colorUtils.js

// Import color databases
import { colorList } from './colorHelper'; // Legacy color list
import { allManufacturerColors, getColorByNameAndManufacturer } from './manufacturerColors'; // New manufacturer colors

// Function to remove HTML tags from input text
export const stripHtmlTags = (text) => {
  return text.replace(/<\/?[^>]+(>|$)/g, ""); // Remove all HTML tags
};

// Normalize item keys for consistent matching
export const normalizeItemKey = (key) => {
  return key
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .toLowerCase()
    .replace(/\s+/g, '') // Remove all spaces
    .trim();
};

// Normalize proposed colors based on language
export const normalizeProposedColors = (colors, lang, itemMapping) => {
  const normalizedColors = {};
  Object.keys(colors).forEach(key => {
    const normalizedKey = normalizeItemKey(key); // Normalize keys
    const translatedKey = itemMapping[lang]?.[normalizedKey] || normalizedKey; // Translate based on language
    normalizedColors[translatedKey] = colors[key];
  });
  return normalizedColors;
};

// Function to normalize text to camelCase format
export const normalizeToCamelCase = (text) => {
  return text
    .normalize("NFD")                  // Separate accents from characters
    .replace(/[\u0300-\u036f]/g, "")    // Remove accents
    .toLowerCase()                     // Convert to lowercase
    .replace(/\s+/g, ' ')              // Replace multiple spaces with a single space
    .trim()                            // Remove leading/trailing spaces
    .split(' ')                        // Split by space to handle multi-word colors
    .map((word, index) => {
      return index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join('');                         // Join into a single camelCase string
};

// Function to normalize for name-only comparison (keep spaces, remove special chars)
export const normalizeColorName = (text) => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

// Consolidated getColorByName function with HTML sanitization
// Now searches manufacturer colors first, then falls back to legacy colors
export const getColorByName = (name) => {
  if (!name) return '#ffffff'; // Default to white if name is missing

  // Remove HTML tags (like <strong>)
  const cleanName = stripHtmlTags(name);

  // Strip out manufacturer code in parentheses if present
  // e.g., "Grey White (RAL 9002)" -> "Grey White"
  const nameWithoutCode = cleanName.replace(/\s*\([^)]+\)\s*$/g, '').trim();

  // Normalize the color name for consistent comparison
  const normalizedName = normalizeColorName(nameWithoutCode);
  
  console.log(`🎨 Searching for color: "${cleanName}" (stripped: "${nameWithoutCode}", normalized: "${normalizedName}")`);

  // 1. Search manufacturer colors first (215+ professional colors)
  const manufacturerColor = allManufacturerColors.find(color => {
    const colorNormalized = normalizeColorName(color.name);
    const matches = colorNormalized === normalizedName;
    if (!matches && normalizedName.includes('cedar')) {
      console.log(`  🔍 Comparing "${nameWithoutCode}" with "${color.name}" (${color.code}): normalized "${normalizedName}" vs "${colorNormalized}" = ${matches}`);
    }
    return matches;
  });

  if (manufacturerColor) {
    console.log(`✅ Found manufacturer color: ${manufacturerColor.name} (${manufacturerColor.code}) - ${manufacturerColor.hex}`);
    return manufacturerColor.hex;
  }
  
  console.warn(`⚠️ Color not found in manufacturer database. Tried: "${nameWithoutCode}" (normalized: "${normalizedName}")`);
  console.log(`Available Gentek colors with "cedar":`, allManufacturerColors.filter(c => c.manufacturer === 'gentek' && c.name.toLowerCase().includes('cedar')));

  // 2. Fall back to legacy colorList (47 basic colors) for backwards compatibility
  const formattedName = normalizeToCamelCase(nameWithoutCode);
  const legacyColor = colorList.find(color =>
    normalizeToCamelCase(color.name) === formattedName ||
    normalizeToCamelCase(color.fr) === formattedName
  );

  if (legacyColor) {
    console.log(`✅ Found legacy color: ${legacyColor.name} - ${legacyColor.color}`);
    return legacyColor.color;
  }

  console.warn(`⚠️ Color not found: "${cleanName}". Returning null.`);
  return null; // Return null if no match is found
};



